import RouteError from '@/components/ui/route-error';
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
  useSearch,
} from '@tanstack/react-router';
import { z } from 'zod';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { Tabs } from '@/components/ui/lib/tabs';
import type { TabsValueChangeDetails } from '@/components/ui/lib/tabs';
import { DICTIONARY } from '@/config/dictionary';
import { requireAuth } from '@/utils/require-auth-before-load';
import { queryClient } from '@/lib/query-client';
import {
  getAdminCountryConfigQueryOptions,
  getAdminServicesConfigQueryOptions,
  getAdminPricingConfigQueryOptions,
} from '@/features/admin-settings/api/admin-api';
import CountryAdminTab from '@/features/admin-settings/components/country-admin-tab';
import ServiceAdminTab from '@/features/admin-settings/components/service-admin-tab';
import FeatureAdminTab from '@/features/admin-settings/components/feature-admin-tab';
import PricingAdminTab from '@/features/admin-settings/components/pricing-admin-tab';
import { getServiceFeatureCatalogQueryOptions } from '@/hooks/use-service-feature-catalog';
import { ADMIN_SETTINGS_TABS, type AdminSettingsTab } from '@/types/route-tabs';

const d = DICTIONARY.adminSettingsPage;

const DashboardAdminSettingsPage = () => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { tab } = useSearch({ from: '/admin-settings' });
  const navigate = useNavigate({ from: '/admin-settings' });

  /**
   * The tab is driven by the URL rather than by Tabs' own state. `defaultValue` is read once
   * at mount, so returning from a child editor — which navigates back with an explicit
   * `?tab=` — landed on the first tab, because the search param arrives after Tabs has
   * already mounted. Controlling the value also puts the open tab in the URL, so a refresh,
   * a shared link, and the route loader's prefetch all agree on which tab is showing.
   */
  const activeTab: AdminSettingsTab = tab ?? ADMIN_SETTINGS_TABS[0];

  const handleTabChange = ({ value }: TabsValueChangeDetails) => {
    // Tabs hands back a bare string; narrowing against the tuple keeps the search param
    // inside the schema's enum without a cast.
    const nextTab = ADMIN_SETTINGS_TABS.find((known) => known === value) ?? ADMIN_SETTINGS_TABS[0];
    // `replace` keeps tab switching out of the history stack, so Back still leaves the page.
    void navigate({ search: { tab: nextTab }, replace: true });
  };

  if (pathname !== '/admin-settings') {
    return <Outlet />;
  }

  return (
    <DashboardRouteLayout>
      <DashboardPageShell title={d.pageTitle} description={d.pageDescription}>
        <Tabs.Root
          value={activeTab}
          onValueChange={handleTabChange}
          variant="line"
          size="sm"
          lazyMount
          unmountOnExit
        >
          <Tabs.List mb="4">
            <Tabs.Trigger
              value={ADMIN_SETTINGS_TABS[0]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.countryAdmin}
            </Tabs.Trigger>
            <Tabs.Trigger
              value={ADMIN_SETTINGS_TABS[1]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.serviceAdmin}
            </Tabs.Trigger>
            <Tabs.Trigger
              value={ADMIN_SETTINGS_TABS[2]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.featureAdmin}
            </Tabs.Trigger>
            <Tabs.Trigger
              value={ADMIN_SETTINGS_TABS[3]}
              _selected={{ fontWeight: 'semibold', color: 'accent' }}
            >
              {d.tabs.pricingAdmin}
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value={ADMIN_SETTINGS_TABS[0]}>
            <CountryAdminTab />
          </Tabs.Content>
          <Tabs.Content value={ADMIN_SETTINGS_TABS[1]}>
            <ServiceAdminTab />
          </Tabs.Content>
          <Tabs.Content value={ADMIN_SETTINGS_TABS[2]}>
            <FeatureAdminTab />
          </Tabs.Content>
          <Tabs.Content value={ADMIN_SETTINGS_TABS[3]}>
            <PricingAdminTab />
          </Tabs.Content>
        </Tabs.Root>
      </DashboardPageShell>
    </DashboardRouteLayout>
  );
};

const adminSettingsSearchSchema = z.object({
  tab: z.enum(ADMIN_SETTINGS_TABS).optional(),
});

export const Route = createFileRoute('/admin-settings')({
  validateSearch: adminSettingsSearchSchema,
  beforeLoad: requireAuth,
  loader: async ({ location }) => {
    const params = new URLSearchParams(location.search);
    const activeTab = (params.get('tab') ?? ADMIN_SETTINGS_TABS[0]) as AdminSettingsTab;

    const prefetchMap: Record<AdminSettingsTab, () => Promise<unknown>> = {
      [ADMIN_SETTINGS_TABS[0]]: () =>
        queryClient.prefetchQuery(getAdminCountryConfigQueryOptions()),
      [ADMIN_SETTINGS_TABS[1]]: () =>
        queryClient.prefetchQuery(getAdminServicesConfigQueryOptions()),
      [ADMIN_SETTINGS_TABS[2]]: () =>
        Promise.allSettled([
          queryClient.prefetchQuery(getAdminServicesConfigQueryOptions()),
          queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions('default')),
        ]),
      [ADMIN_SETTINGS_TABS[3]]: () =>
        Promise.allSettled([
          queryClient.prefetchQuery(getAdminPricingConfigQueryOptions()),
          queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions('default')),
        ]),
    };

    await prefetchMap[activeTab]();
    return null;
  },
  component: DashboardAdminSettingsPage,
  errorComponent: RouteError,
});
